<script>
/**
 * Print-friendly table with sticky thead (repeats per printed page),
 * page-break-inside avoid on rows, automatic right-alignment for `type: "number"`
 * columns, and tabular-nums for all cells.
 *
 * Columns: array of `{ key, label, type?, width?, formatter?, align? }`.
 *   - type "number" → right-aligned, tabular-nums
 *   - formatter(value, row) → returns rendered string
 *   - width: "20mm" or "15%" — passed to colgroup
 *   - align: "left" | "right" | "center" — overrides type-based alignment
 *
 * Slots:
 *   - cell-{key}: `{ value, row, index }` — fully custom cell rendering
 *   - row: `{ row, index }` — fully custom row rendering (skips column rendering)
 */
export default {
  name: "PrintTable",
  props: {
    columns: { type: Array, required: true },
    rows: { type: Array, default: () => [] },
    rowKey: { type: [String, Function], default: "id" },
    zebra: { type: Boolean, default: false },
    compact: { type: Boolean, default: false },
    emptyText: { type: String, default: "No items" },
  },
  computed: {
    classes() {
      return ["print-table", { zebra: this.zebra, compact: this.compact }];
    },
  },
  methods: {
    keyFor(row, index) {
      if (typeof this.rowKey === "function") return this.rowKey(row, index);
      return row?.[this.rowKey] ?? index;
    },
    cellValue(col, row) {
      const raw = row?.[col.key];
      if (col.formatter) return col.formatter(raw, row);
      return raw;
    },
    cellClasses(col) {
      const cls = [];
      const align = col.align || (col.type === "number" ? "right" : null);
      if (align === "right") cls.push("print-num");
      else if (align === "center") cls.push("print-center");
      return cls;
    },
  },
};
</script>

<template>
  <table :class="classes">
    <colgroup v-if="columns.some((c) => c.width)">
      <col v-for="col in columns" :key="col.key" :style="col.width ? { width: col.width } : null" />
    </colgroup>
    <thead>
      <tr>
        <th
          v-for="col in columns"
          :key="col.key"
          :class="cellClasses(col)"
        >
          {{ col.label }}
        </th>
      </tr>
    </thead>
    <tbody>
      <template v-if="rows.length">
        <tr v-for="(row, index) in rows" :key="keyFor(row, index)">
          <slot name="row" :row="row" :index="index">
            <td v-for="col in columns" :key="col.key" :class="cellClasses(col)">
              <slot :name="`cell-${col.key}`" :value="cellValue(col, row)" :row="row" :index="index">
                {{ cellValue(col, row) }}
              </slot>
            </td>
          </slot>
        </tr>
      </template>
      <tr v-else>
        <td :colspan="columns.length" class="print-center print-muted">{{ emptyText }}</td>
      </tr>
    </tbody>
    <tfoot v-if="$slots.foot">
      <slot name="foot" />
    </tfoot>
  </table>
</template>
